import { Button } from '@mui/material'
import { jwtDecode } from 'jwt-decode'
import { SnackbarKey, useSnackbar } from 'notistack'
import { AuthToken } from '../models/auth'
import { UserDTO, UserEducationalHistoryDTO, UserWorkHistoryDTO } from '../models/users'
import { TEducationalInfo, TProfessionalInfo } from '../hooks/useFormRegister'
import { Frequency, JobPeriod, Modality } from '../models/post'
import { months } from './enums'

export const getEnumKeyByValue = (object: object, value: string | number) =>
    Object.keys(object).find((key) => object[key as keyof typeof object] === value)

export const dateFormatter = (input: Date) => {
    const inputDate = input.getUTCDate()
    const inputMonth = input.getUTCMonth() + 1

    const date = inputDate < 10 ? `0${inputDate}` : inputDate
    const month = inputMonth < 10 ? `0${inputMonth}` : inputMonth
    const year = input.getUTCFullYear()

    return `${date}/${month}/${year}`
}

export const dateTimeFormatter = (input: Date) => {
    const inputDate = input.getUTCDate()
    const inputMonth = input.getUTCMonth() + 1
    const inputHours = input.getHours()
    const inputMinutes = input.getMinutes()

    const date = inputDate < 10 ? `0${inputDate}` : inputDate
    const month = inputMonth < 10 ? `0${inputMonth}` : inputMonth
    const year = input.getUTCFullYear()
    const hours = inputHours < 10 ? `0${inputHours}` : inputHours
    const minutes = inputMinutes < 10 ? `0${inputMinutes}` : inputMinutes

    return `${date}/${month}/${year} ${hours}:${minutes}`
}

export const formatDateStringToLocalDate = (input: string) => {
    const year = input.substring(6, 10)
    const month = input.substring(3, 5)
    const day = input.substring(0, 2)

    return `${year}-${month}-${day}`
}

export const withoutCommasOrDots = (value: string) => {
    return parseFloat(value.replace(/\./g, '').replace(',', ''))
}

export const dateParser: (dateDD_MM_YYYY: string) => Date = (dateDD_MM_YYYY) => {
    const year = parseInt(dateDD_MM_YYYY.substring(6, 10), 10)
    const month = parseInt(dateDD_MM_YYYY.substring(3, 5), 10) - 1
    const day = parseInt(dateDD_MM_YYYY.substring(0, 2), 10)

    return new Date(year, month, day)
}

export const getDate = (stringDate: string) => {
    const date = new Date(stringDate)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()

    return `${day}/${month}/${year}`
}

export const isDateToday = (date: Date) => {
    return date.setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)
}

export const formatDaysToYearsAndMonths = (days: number) => {
    const years = Math.floor(days / 365)
    const daysMinusYears = days % 365
    const months = Math.floor(daysMinusYears / 30)
    const daysMinusMonths = daysMinusYears % 30

    const parts: string[] = []
    if (years > 0) parts.push(`${years} ${years === 1 ? 'año' : 'años'}`)
    if (months > 0) parts.push(`${months} ${months === 1 ? 'mes' : 'meses'}`)
    if (daysMinusMonths > 0)
        parts.push(`${daysMinusMonths} ${daysMinusMonths === 1 ? 'día' : 'días'}`)

    return parts.join(', ').replace(/, ([^,]*)$/, ' y $1')
}

export const validateEmail = (email: string) =>
    email
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        )

export function validateFormInfo<T extends Object>(info: T) {
    const fieldsNonEmpty = Object.entries(info).every(([key, value]) => {
        if (key === 'id' || key === 'endingDate' || key === 'companyName') return true
        return value !== ''
    })

    return fieldsNonEmpty
}

export const validatePassword = (password: string) => {
    const regex: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/
    const noSpaces = /^\S*$/

    return regex.test(password) && noSpaces.test(password)
}

export const containsLowercase = (str: string) => {
    return /[a-z]/.test(str)
}

export const containsUppercase = (str: string) => {
    return /[A-Z]/.test(str)
}

export const containsNumber = (str: string) => {
    return /\d/.test(str)
}

export const containsSpecialCharacter = (str: string) => {
    return /[!@#$%^&*]/.test(str)
}

export const containsNotBlankSpace = (str: string) => {
    return !/\s/.test(str)
}

export const validateNumber = (value: string) => {
    return /^\d+$/.test(value) && (!isNaN(parseInt(value)) || !isNaN(parseFloat(value)))
}

export const normalizeString = (input: string) =>
    input
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

export const CloseSnackbarButton = ({ bar }: { bar: SnackbarKey }) => {
    const { closeSnackbar } = useSnackbar()
    return (
        <Button
            onClick={() => closeSnackbar(bar)}
            className="absolute left-0 top-0 w-full h-full"
        />
    )
}

export const sortDates = (first: Date | null, second: Date | null) => {
    if (!first) return -1
    if (!second) return 1
    return new Date(second).getTime() - new Date(first).getTime()
}

export const isAdmin = () => {
    const token = localStorage.getItem('_auth')
    if (!token) return false
    const decodedToken: AuthToken = jwtDecode(token)
    return decodedToken.isAdmin
}

export const ROOT_ADMIN_EMAIL = 'admin@gmail.com'
export const isRootAdmin = () => {
    const token = localStorage.getItem('_auth')
    if (!token) return false
    const decodedToken: AuthToken = jwtDecode(token)
    return decodedToken.isAdmin && decodedToken.sub === ROOT_ADMIN_EMAIL
}

export const isCurrentUser = (user: UserDTO) => {
    const token = localStorage.getItem('_auth')
    if (!token) return false
    const decodedToken: AuthToken = jwtDecode(token)
    return decodedToken.sub === user.email
}

export const mapUserEducationDTOtoEducationInfo = (
    info: UserEducationalHistoryDTO[],
): TEducationalInfo[] => {
    return info.map(
        (obj): TEducationalInfo => ({
            id: obj.school,
            degree: obj.degree,
            school: obj.school,
            carrer: obj.areaOfStudy,
            startDate: dateFormatter(new Date(obj.startDate)),
            endingDate: obj.endDate ? dateFormatter(new Date(obj.endDate)) : 'Actualidad',
            country: obj.country,
        }),
    )
}

export const mapUserWorkDTOtoProfessionalInfo = (
    info: UserWorkHistoryDTO[],
): TProfessionalInfo[] => {
    return info.map(
        (obj): TProfessionalInfo => ({
            id: obj.employerName,
            companyName: obj.employerName,
            job: obj.jobTitle,
            startDate: dateFormatter(new Date(obj.startDate)),
            endingDate: obj.endDate ? dateFormatter(new Date(obj.endDate)) : 'Actualidad',
            responsabilities: obj.description,
            country: obj.employerCountry,
        }),
    )
}

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export const scrollToChildInsideParent = (parentId: string, childId: string) => {
    const parent = document.getElementById(parentId)
    const child = document.getElementById(childId)
    if (!parent || !child) return

    const parentPosition = parent.getBoundingClientRect()
    const childPosition = child.getBoundingClientRect()
    const parentViewableArea = {
        height: parent.clientHeight,
        width: parent.clientWidth,
    }

    const isViewable =
        childPosition.top >= parentPosition.top &&
        childPosition.top <= parentPosition.top + parentViewableArea.height

    if (!isViewable) parent.scrollTop = childPosition.top + parent.scrollTop - parentPosition.top
}

export const translateStringToEnumModality = (input: string): string => {
    switch (input) {
        case 'Presencial':
            return Modality.IN_PERSON
        case 'Remoto':
            return Modality.REMOTE
        case 'Híbrido':
            return Modality.HYBRID
        default:
            return ''
    }
}

export const translateStringToEnumFrequency = (input: string): string => {
    switch (input) {
        case 'Por hora':
            return Frequency.HOURLY
        case 'Semanal':
            return Frequency.WEEKLY
        case 'Mensual':
            return Frequency.MONTHLY
        case 'Anual':
            return Frequency.YEARLY
        default:
            return ''
    }
}

export const translateEnumModalityToString = (modality: Modality): string => {
    switch (modality) {
        case Modality.IN_PERSON:
            return 'Presencial'
        case Modality.REMOTE:
            return 'Remoto'
        case Modality.HYBRID:
            return 'Híbrido'
        default:
            return ''
    }
}

export const translateEnumFrequencyToString = (frequency: Frequency): string => {
    switch (frequency) {
        case Frequency.HOURLY:
            return 'Por hora'
        case Frequency.WEEKLY:
            return 'Semanal'
        case Frequency.MONTHLY:
            return 'Mensual'
        case Frequency.YEARLY:
            return 'Anual'
        default:
            return ''
    }
}

export const translateEnumJobPeriodToString = (jobPeriod: JobPeriod): string => {
    switch (jobPeriod) {
        case JobPeriod.FULL_TIME:
            return 'Full Time'
        case JobPeriod.PART_TIME:
            return 'Part Time'
        case JobPeriod.INTERNSHIP:
            return 'Pasantía'
        default:
            return ''
    }
}

export const translateStringToEnumJobPeriod = (jobPeriod: string): JobPeriod => {
    switch (jobPeriod) {
        case 'Full Time':
            return JobPeriod.FULL_TIME
        case 'Part Time':
            return JobPeriod.PART_TIME
        default:
            return JobPeriod.INTERNSHIP
    }
}

export const upperCaseWords = (str: string) => {
    const words = str.split(' ')

    const capitalizedWords = words.map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1)
    })

    return capitalizedWords.join(' ')
}

export const lowerCaseWords = (str: string) => {
    if (!str) return ''

    const words = str.split(' ')

    const capitalizedWords = words.map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })

    return capitalizedWords.join(' ')
}

export const formatDateToOnlyMonthAndYear = (dateDDMMYYYY: string) => {
    return `${months[Number(dateDDMMYYYY.slice(3, 5)) as keyof typeof months]} ${dateDDMMYYYY.slice(6, 10)}`
}

export const splitArrayIntoChunks = (array: any[], size: number) => {
    const result: any[] = []
    for (let i = 0; i < array.length; i += size) {
        const chunk = array.slice(i, i + size)
        result.push(chunk)
    }
    return result
}

export const removeDuplicatesFromArray = (array: any[]) => {
    return array.filter(function (item, pos, self) {
        return self.indexOf(item) === pos
    })
}
